import config from '../config'
import validUrl from 'valid-url'
import { setItem } from './localStorage'
import MobileDetect from 'mobile-detect'

let shouldDisplayLocationPermissionError = false
/**
 * Get position in latitude & longitude using the navigator's geolocation
 * @param {Object} options - options to get the geolocation such as accuracy, timeout and maximumAge
 * @returns {Object}
 */
const getPosition = options => {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options)
  })
}

/**
 * Handle geolocation by asking the permission from the user
 * @param {Object} $store - Vue store
 * @param {String} mainContent
 * @param {String} source
 * @param {String} errorMessage
 */
const handleGeoLocation = async ($store, mainContent, source, errorMessage) => {
  try {
    const position = await getPosition({
      enableHighAccuracy: true,
      timeout: config.LOCATION_PERMISSION_TIMEOUT,
      maximumAge: 0
    })

    $store.dispatch('socket_locationSend', {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      message: mainContent,
      source
    })
  } catch (err) {
    console.error('Error: Location access denied or timed out', err)
    if (shouldDisplayLocationPermissionError) {
      $store.dispatch('set_error_message', errorMessage)
    }

    // Show error message if the QR clicked for second time or more
    shouldDisplayLocationPermissionError = true
    return
  }
}

/**
 * Check if the provided execFunction is valid or not
 * @param {String} execFunc
 * @returns {Boolean}
 */
const checkIfExecFuncIsValid = execFunc => {
  if (!execFunc) {
    return false
  }

  try {
    new Function(execFunc)
    return true
  } catch (error) {
    return false
  }
}

/**
 * Handle exec js function type QR
 * @param {Object} $store - Vue store
 * @param {String} execFunc
 */
const handleExecFunc = ($store, execFunc) => {
  if (!checkIfExecFuncIsValid(execFunc)) {
    console.error('handleExecFunc error invalid execFunc')
    return
  }

  // Send tracking info for the click on link
  const data = [
    {
      event: {
        eventType: config.EVENT_TYPES.CLICK,
        element: config.EVENT_ELEMENTS.WEBCHAT_EXEC_JS_FUNC
      },
      inputType: config.INPUT_TYPES.TOUCH
    }
  ]
  $store.dispatch('socket_userTracking', data)

  parent.postMessage({ event: config.FRAME_EVENT_TYPES.SENT.EXEC_JS_FUNC, data: execFunc }, '*')
}

/**
 * Check the button/menu is geo location helper
 * @param {Object} payload
 * @param {String} payload.intentId
 * @param {String} payload.url
 * @param {String} payload.helperType
 * @returns {Boolean}
 */
const isGeoLocationHelper = payload => {
  return payload && payload.hasOwnProperty('helperType') && payload.helperType === config.HELPER_TYPES.GEOLOCATION
}

/**
 * Check the button/menu is Exec JS Function helper
 * @param {Object} payload
 * @param {String} payload.execFunc
 * @param {String} payload.helperType
 * @returns {Boolean}
 */
const isExecJSFuncHelper = payload => {
  return payload && payload.hasOwnProperty('helperType') && payload.helperType === config.HELPER_TYPES.EXEC_JS_FUNC
}

/**
 * Check the payload url is valid
 *
 * @param {Object} payload
 * @param {Boolean} isKiosk
 * @returns {Boolean}
 */
const isValidPayloadUrl = (payload, isKiosk) => {
  return (
    !isKiosk &&
    payload &&
    payload.url &&
    (validUrl.isWebUri(payload.url) || payload.url.match(config.QUICK_REPLY_URL_REGEX))
  )
}

/**
 * Get the host domain without the sub domain part
 * @param {String} url
 * @param {String} [subdomain=SUBDOMAIN_PART]
 * @returns {String}
 */
const getHostWithoutSubdomainFromUrl = (url, subdomain = config.SUBDOMAIN_PART) => {
  const domain = url && new URL(url)
  return (domain && domain.host && domain.host.replace(subdomain, '')) || ''
}

/**
 * Get the parent host
 * @returns {String} url
 */
const getParentHost = () => {
  if (window.location === window.parent.location) {
    return document.location.host
  }

  return document.referrer
}

/**
 * Get link target based on the url
 * @param {String} url
 * @returns {String}
 */
const getLinkTarget = url => {
  const targetHost = getHostWithoutSubdomainFromUrl(url)

  const currentParentHost = getHostWithoutSubdomainFromUrl(getParentHost())

  if (targetHost && currentParentHost && targetHost === currentParentHost) {
    return config.TARGET_PARENT
  }

  return config.TARGET_BLANK
}

/**
 * Set the local storage popin status to closed when opening a new link, so that chat window will be closed in the new window
 * And focus on the newly opened window
 * @params {String} assistantId
 * @params {Object} win - window object
 */
const focusNewWindowAndClosePopin = (assistantId, win) => {
  setItem(`popin_status_${assistantId}`, config.POPIN_STATUS.CLOSED)
  win.focus()
}
/**
 * Open the link in new tab
 * @param {String} assistantId
 * @param {String} url
 */
const openLink = (assistantId, url) => {
  try {
    const target = getLinkTarget(url)
    const win = window.open(url.trim(), target)
    const md = new MobileDetect(navigator.userAgent)
    const isMobile = md.mobile() !== null || md.tablet() !== null

    if (target === config.TARGET_BLANK) {
      win.focus()
      return
    }

    if (!isMobile) {
      return
    }

    // CF issue #505 we only want to close the popin
    // If we stay on the same domain (TARGET_PARENT) and are on mobile
    setItem(`popin_status_${assistantId}`, config.POPIN_STATUS.CLOSED)
  } catch (err) {
    console.error('openLink error', err)
    const win = window.open(url.trim(), config.TARGET_BLANK)
    focusNewWindowAndClosePopin(assistantId, win)
  }
}

/**
 * Track the menu link clicked
 * @param {Object} $store - Vue store
 * @param {String} mainContent
 * @param {String} url
 * @param {String} elementType
 * @param {String} conversationId
 */
const trackLink = ($store, mainContent, url, elementType, conversationId) => {
  // Send tracking info for the click on link
  const data = [
    {
      event: {
        eventType: config.EVENT_TYPES.CLICK,
        element: elementType,
        value: url.trim()
      },
      mainContent: mainContent,
      inputType: config.INPUT_TYPES.TOUCH,
      conversationId
    }
  ]
  $store.dispatch('socket_userTracking', data)
}

/**
 * Send quick reply text message to bot
 * @param {Object} $store - Vue store
 * @param {Object} payload
 * @param {String} payload.intentId
 * @param {String} payload.url
 * @param {String} payload.helperType
 * @param {String} mainContent
 * @param {String} source
 */
const sendQRText = ($store, payload, mainContent, source) => {
  const message = [
    {
      messageType: config.MESSAGE_TYPES.MESSAGE,
      mainContent: mainContent,
      payload: payload,
      inputType: config.INPUT_TYPES.TOUCH,
      source
    }
  ]

  $store.dispatch('socket_userMessage', message)
}

/**
 * Send message from chip
 * @param {Object} $store - Vue store
 * @param {Object} payload
 * @param {String} payload.intentId
 * @param {String} payload.url
 * @param {String} payload.helperType
 * @param {String} mainContent
 * @param {String} source
 * @param {String} assistantId
 * @param {String} errorMessage
 * @param {String} elementType - element which is going to be tracked menu link or link from quick reply
 * @param {Boolean} isKiosk
 * @param {String} conversationId
 */
export const sendMessage = (
  $store,
  payload,
  mainContent,
  source,
  assistantId,
  errorMessage,
  elementType,
  isKiosk,
  conversationId = null
) => {
  if (!payload) {
    // send simple text QR
    sendQRText($store, payload, mainContent, source)
    return
  }

  // Handle geo location QR
  if (isGeoLocationHelper(payload)) {
    handleGeoLocation($store, mainContent, source, errorMessage)
    return
  }

  // Handle Exec JS Type QR
  if (isExecJSFuncHelper(payload)) {
    handleExecFunc($store, payload.execFunc)
    return
  }

  // Handle link QR
  if (isValidPayloadUrl(payload, isKiosk)) {
    openLink(assistantId, payload.url)
    trackLink($store, mainContent, payload.url, elementType, conversationId)
    return
  }

  // Default handling of QR
  sendQRText($store, payload, mainContent, source)
}

/**
 * Check the chip should be displayed
 * @param {Object} payload
 * @returns {Boolean}
 */
export const shouldDisplayChip = payload => {
  return !isGeoLocationHelper(payload) || !!navigator.geolocation
}
